/**
 * The client half of the `/host/my-kanban` conditional request (ENG-9584).
 *
 * WHY THIS ENDPOINT. `/host/my-kanban` is 412,971 requests/24h on prod — twice
 * the rate of every other endpoint in the poll cluster — and it is the one lane
 * ENG-9585's fetch spacing deliberately left ungated: its Realtime lane injects
 * a kanban-work nudge directly rather than calling `triggerEarlyPoll`, so there
 * is no push that would make a slower fetch safe. Request count is the part of
 * this endpoint's cost that cannot be spaced away, so the bytes are what is
 * left to take.
 *
 * HOW IT WORKS. The server hashes the board it just built and returns that as
 * `payload_token`. The next request echoes it as `known_token`; when the board
 * still hashes the same the server answers `{ unchanged: true }` and we serve
 * the rows we already hold. The token is derived from the response on EVERY
 * request, never from a stored version — so it cannot be stale-but-matching,
 * which is the one failure the acceptance criteria single out ("a
 * stale-but-matching token ... would silently freeze config delivery").
 *
 * WHY IT HOLDS RAW ROWS AND RE-SANITISES ON EVERY SERVE. `sanitizeBoardItem`
 * is pure and returns fresh objects, so re-running it hands each caller its own
 * copy. Handing out the SAME object array instead would make this cache
 * aliased: `/host/my-kanban`'s three consumers (the board-template inject, the
 * hybrid reconcile, and the newly-done diff) all receive the array, and any one
 * of them mutating a card in place would silently rewrite what the other two
 * see on every later tick — a corruption with no fetch, no error, and no way to
 * tell it from a real board change. Re-mapping ~70 rows is far cheaper than the
 * round trip it replaces.
 *
 * WHAT THIS IS NOT. It is NOT `kanbanBoardCache`, which is a deliberately-stale
 * fallback for a FAILED fetch and drives `boardItemsStale` (the ENG-7557 /
 * ENG-7958 nudge guard). A board served from here is the CURRENT board,
 * confirmed unchanged by the server this tick — so it must not set that flag.
 * Conflating the two would either make every unchanged tick look stale (killing
 * the nudge guard's meaning) or make a genuinely failed fetch look fresh.
 */

/** What the server returns. `unchanged` and `items` are mutually exclusive. */
export type BoardResponse<T> = {
  items?: T[];
  unchanged?: boolean;
  payload_token?: string;
  active_truncated?: boolean;
};

type Entry<T> = { token: string; rows: T[] };

/**
 * What the conditional request actually did, per poll cycle (ENG-9697).
 *
 * WHY THE CACHE OWNS THIS AND NOT THE CALL SITE. `resolve` is the only place
 * that decides whether a response was a hit, and the decision is one line
 * (`res.unchanged === true` over a held board). A caller that re-read that
 * field to count it would be a SECOND implementation of the same decision,
 * free to drift from the first — the shape ENG-9584 deliberately avoided by
 * putting the memories decision in `conditional-fetch.ts` rather than inline.
 * Counting where the branch is taken makes the number correct by construction.
 *
 * `refetch` is deliberately separate from `full` rather than folded into it.
 * Both are misses, but a refetch is the teardown race — the server answered
 * `unchanged` against a board we no longer hold — and it costs TWO round trips
 * rather than one. Folding it in would hide the only signal that the race is
 * firing in the wild, behind a number that looks like ordinary miss traffic.
 * It is expected to read 0.
 */
export interface BoardConditionalStats {
  /** Served from the held board: no rows crossed the wire. */
  unchanged: number;
  /** Rows crossed the wire. Includes the second half of a `refetch`. */
  full: number;
  /** `unchanged` arrived against a board we no longer held. Expected 0. */
  refetch: number;
}

/**
 * Per-agent last-known board, keyed by agent id.
 *
 * Keyed on agent id rather than code name because that is what
 * `clearAgentCaches` is given first and what survives a rename (ENG-7854: a
 * code_name rename is a supervised re-addressing, the agent id is fixed).
 */
export class BoardTokenCache<T> {
  private entries = new Map<string, Entry<T>>();
  private counts: BoardConditionalStats = { unchanged: 0, full: 0, refetch: 0 };

  /**
   * Counts since the last {@link resetStats}. Read once per poll cycle for the
   * `[poll-cycle]` log line — the ONLY place this feature's hit rate is
   * visible. Server-side it is unmeasurable: in the API Gateway access log an
   * `{ unchanged: true }` response and a genuinely empty board are the same
   * ~90 bytes, so a token that never matched would present as a fleet of agents
   * with empty boards rather than as a saving that is not being taken.
   */
  stats(): BoardConditionalStats {
    return { ...this.counts };
  }

  /** Zero the counters. Called at the top of each poll cycle. */
  resetStats(): void {
    this.counts = { unchanged: 0, full: 0, refetch: 0 };
  }

  /** The token to send as `known_token`, or undefined when we hold no board. */
  tokenFor(agentId: string): string | undefined {
    return this.entries.get(agentId)?.token;
  }

  /**
   * Resolve a response into rows.
   *
   * Returns `null` when the server said `unchanged` but we hold nothing for
   * this agent — which should be impossible (we only send a token we have) but
   * is reachable across a teardown/re-add race, and the caller must re-fetch
   * rather than silently serve an empty board. An empty board is not a harmless
   * default here: section 12 writes whatever it is handed into `notifyBoardCache`
   * as this tick's truth, so seeding "nothing is done" over a board that has
   * done cards makes the NEXT tick report every one of them as newly done — a
   * burst of false completion notifications to real users.
   */
  resolve(agentId: string, res: BoardResponse<T>): T[] | null {
    if (res.unchanged === true) {
      const held = this.entries.get(agentId);
      if (!held) {
        this.counts.refetch += 1;
        return null;
      }
      this.counts.unchanged += 1;
      // Refresh the token if the server moved it (it echoes the same value
      // today; this costs nothing and stops a future change from stranding us).
      if (typeof res.payload_token === 'string' && res.payload_token.length > 0) {
        this.entries.set(agentId, { token: res.payload_token, rows: held.rows });
      }
      return held.rows;
    }

    this.counts.full += 1;
    const rows = res.items ?? [];
    // Only remember a board we can address. An API that predates ENG-9584
    // returns no `payload_token`; we then hold nothing, send nothing, and behave
    // exactly as before — the compatibility direction that matters during a
    // rollout, since the CLI reaches hosts before every stage has redeployed.
    if (typeof res.payload_token === 'string' && res.payload_token.length > 0) {
      this.entries.set(agentId, { token: res.payload_token, rows });
    } else {
      this.entries.delete(agentId);
    }
    return rows;
  }

  /**
   * Drop an agent's board. Teardown must call this: a board left behind is
   * served to the agent if it is later RE-ADDED, which is exactly when its work
   * is most likely to have moved on. Same argument as ENG-9585's
   * `lastRefreshPayload` purge, and the same reason it is registered in
   * `clearAgentCaches` rather than deleted ad hoc at each call site.
   */
  forget(agentId: string): void {
    this.entries.delete(agentId);
  }

  /** Test/diagnostic only. */
  size(): number {
    return this.entries.size;
  }
}
